import React, { FunctionComponent, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";

import useMap from "./use-map";
import { Evented, Control } from "mapbox-gl";

class DefaultMapControl extends Evented {
  private element: Element;

  constructor(element: HTMLElement) {
    super();

    this.element = element;
  }

  onAdd() {
    return this.element;
  }

  onRemove() {
    return this.element;
  }
}

const MapControl: FunctionComponent<MapControlProps> = ({
  children,
  mapRef,
  position = "top-right",
  control,
  defaultStyles = true,
}) => {
  // Create the HTML element (and portal)
  const htmlElement = useMemo(() => {
    const div = document.createElement("div");
    div.className = "mapboxgl-ctrl";
    if (defaultStyles) {
      div.className += " mapboxgl-ctrl-group";
    }
    return div;
  }, [defaultStyles]);

  // Resolve the control to be added
  const resolvedControl = useMemo(() => {
    if (control) {
      return control;
    }

    return new DefaultMapControl(htmlElement);
  }, [control, htmlElement]);

  // Obtain the map
  const { map } = useMap();

  // Register...
  useEffect(() => {
    if (map && resolvedControl) {
      // Add the control to the map at the right location
      map.addControl(resolvedControl as Control, position);

      return () => {
        // Remove the control
        map.removeControl(resolvedControl as Control);
      };
    }
  }, [map, resolvedControl, position]);

  return ReactDOM.createPortal(children, htmlElement);
};

type MapControlProps = {
  children?: React.ReactElement;
  mapRef?: string;
  position?: "top-left" | "top-right" | "bottom-right" | "bottom-left";
  control?: Evented;
  defaultStyles?: boolean;
};

export default React.memo(MapControl);
